"use client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from "@fluentui/react-components";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SelectionPersistenceKeys } from "../../constants/SelectionPersistenceKeys";
import { useGetActivityFields } from "../../hooks/useActivities";
import {
  useGetImageFields,
  useGetImageQuickEditFields,
  useUpdateImageDrafts,
} from "../../hooks/useImages";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Field } from "../../models/Field";
import {
  Image,
  UpdateImagesMetadata,
  UpdateImagesRequest,
} from "../../models/Image";
import { mergeImagesFieldsWithAssociatedActivitiesFields } from "../../services/ImageService";
import { ReportType } from "../../services/ReportService";
import { getImageColumnsFromColumnOrder } from "../../services/ViewService";
import {
  PaginatedList,
  PaginatedListEditingOptions,
} from "../list/PaginatedList";

const DraftActivityImagesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [archiveBlockedDialogOpen, setArchiveBlockedDialogOpen] =
    useState(false);

  const { mutateAsync: updateImages, isPending: updateImagesPending } =
    useUpdateImageDrafts();

  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetImageFields();
  const { data: quickEditViewDefinition } = useGetImageQuickEditFields();
  const {
    data: activityViewDefinition,
    isLoading: activityViewDefinitionLoading,
  } = useGetActivityFields();

  const handleEdit = useCallback(
    (imageId: string) => {
      navigate(`${ClientRoutes.MediaActivityEdit}/${imageId}`);
    },
    [navigate]
  );

  const columns = useMemo(() => {
    if (
      viewDefinitionLoading ||
      !viewDefinition ||
      !activityViewDefinition ||
      activityViewDefinitionLoading
    ) {
      return [];
    }

    return getImageColumnsFromColumnOrder(
      viewDefinition.standardFields,
      viewDefinition.fields,
      activityViewDefinition.fields,
      viewDefinition.draftsFieldOrder ?? [],
      handleEdit,
      true, // Activity context
      true // Draft context
    );
  }, [
    viewDefinition,
    viewDefinitionLoading,
    activityViewDefinition,
    activityViewDefinitionLoading,
    handleEdit,
  ]);

  const mapItemToRequest = (item: Image): UpdateImagesMetadata => {
    const isArchived = item.fieldValues?.booleans.archived ?? item.isArchived;
    const isUsedInPublishedActivities = (item.activities?.length ?? 0) > 0;
    const canArchive = !isUsedInPublishedActivities || item.isArchived;
    const guardedIsArchived = canArchive ? isArchived : false;
    const isActive = guardedIsArchived
      ? false
      : (item.fieldValues?.booleans.active ?? item.isActive);

    return {
      versionId: item.versionId,
      title: item.title,
      isActive,
      isArchived: guardedIsArchived,
    };
  };

  const isFieldChangeBlocked = (image: Image, field: Field, value: unknown) => {
    const isArchivedField = field.uniqueName === "archived";
    const isUsedInPublishedActivities = (image.activities?.length ?? 0) > 0;
    const isTryingToArchive = value === true;

    return (
      isArchivedField &&
      isTryingToArchive &&
      isUsedInPublishedActivities &&
      !image.isArchived
    );
  };

  const onFieldChangeBlocked = () => {
    setArchiveBlockedDialogOpen(true);
  };

  const onClickSave = async (itemsToSave: Image[]) => {
    const request: UpdateImagesRequest = {
      imageMetadata: itemsToSave.map(mapItemToRequest),
    };
    await updateImages(request);
  };

  const editOptions: PaginatedListEditingOptions<Image> = {
    createFormRoute: ClientRoutes.MediaActivityCreate,
    allowQuickEdit: true,
    showFileNameColumn: true,
    onSave: onClickSave,
    savePending: updateImagesPending,
    viewDefinition: quickEditViewDefinition,
    entityType: "image",
    isFieldChangeBlocked,
    onFieldChangeBlocked,
  };

  return (
    <>
      <PaginatedList<Image>
        apiEndpoint={ApiEndpoints.ImageDrafts}
        columns={columns}
        defaultSortAscending={false}
        defaultSortColumnName={"modified"}
        searchPlaceholder={t("searchImage")}
        cacheKey={ApiEndpoints.ImageDrafts}
        filtersEndpoint={ApiEndpoints.ImageFilters}
        editingOptions={editOptions}
        postQueryTransformation={
          mergeImagesFieldsWithAssociatedActivitiesFields
        }
        reportType={ReportType.DraftProcessImages}
        storageKey="images:activity:drafts"
        libraryFilterScope={{ listType: "image" }}
        selectionPersistenceKey={SelectionPersistenceKeys.ImagesActivityDrafts}
      />
      <Dialog
        open={archiveBlockedDialogOpen}
        onOpenChange={(_, data) => setArchiveBlockedDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t("imageUsedInPublishedActivitiesTitle")}
            </DialogTitle>
            <DialogContent>
              {t("imageUsedInPublishedActivitiesMessage")}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setArchiveBlockedDialogOpen(false)}>
                {t("close")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default DraftActivityImagesList;
